import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { setRealtimeAccessTokenFactory } from '../realtimeHub';

const AuthContext = createContext(null);

function normalizeRoleNames(values) {
    const canonicalRoleMap = {
        admin: 'Admin',
        commentary: 'Commentary',
        startbarge: 'StartBarge',
        finishtent: 'FinishTent',
        registration: 'Registration',
        sponsoradmin: 'SponsorAdmin'
    };

    const rawValues = Array.isArray(values) ? values : [];
    const normalized = rawValues
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .map((role) => {
            const key = role.toLowerCase().replace(/[^a-z0-9]+/g, '');
            return canonicalRoleMap[key] || role;
        });

    return [...new Set(normalized)];
}

export function AuthProvider({ children }) {
    const [session, setSession] = useState(undefined); // undefined = initialising
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const rolesLoadedForToken = useRef(null);

    const clearCachedSettingsForSignedOutUser = useCallback(() => {
        const localStorageKeys = [
            'app-sidebar-collapsed',
            'regatta26'
        ];
        const sessionStorageKeys = [
            'regatta.oauth.returnUrl',
            'regatta26'
        ];

        try {
            localStorageKeys.forEach((key) => window.localStorage.removeItem(key));

            const keysToRemove = [];
            for (let i = 0; i < window.localStorage.length; i += 1) {
                const key = window.localStorage.key(i);
                if (!key) continue;
                if (key.startsWith('commentary-hidden-')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((key) => window.localStorage.removeItem(key));
        } catch {
            // Ignore storage cleanup errors in restricted browser modes.
        }

        try {
            sessionStorageKeys.forEach((key) => window.sessionStorage.removeItem(key));
        } catch {
            // Ignore storage cleanup errors in restricted browser modes.
        }
    }, []);

    const fetchRoles = useCallback(async (accessToken) => {
        if (!accessToken || rolesLoadedForToken.current === accessToken) return;
        setRolesLoading(true);
        try {
            const res = await fetch('/api/auth/me', {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (!res.ok) {
                throw new Error(`Role fetch failed with HTTP ${res.status}`);
            }
            let fetched = [];
            const data = await res.json();
            fetched = normalizeRoleNames(data.roles);
            // If role response is empty, allow follow-up retries on focus/interval.
            rolesLoadedForToken.current = fetched.length > 0 ? accessToken : null;
            setRoles(fetched);
        } catch {
            // Keep authenticated session, but allow retry on the same token.
            rolesLoadedForToken.current = null;
            setRoles([]);
        } finally {
            setRolesLoading(false);
        }
    }, []);

    useEffect(() => {
        // Restore any existing session on mount (page reload / tab restore)
        supabase.auth.getSession().then(({ data: { session } }) => {
            const restoredSession = session ?? null;
            const token = restoredSession?.access_token || null;
            if (token && rolesLoadedForToken.current !== token) {
                setRolesLoading(true);
            }

            setSession(restoredSession);
            if (token) {
                fetchRoles(token);
            }
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'TOKEN_REFRESHED') {
                // Token silently renewed — update session but keep existing roles
                setSession(session);
                if (session?.access_token && rolesLoadedForToken.current !== session.access_token) {
                    setRolesLoading(true);
                    fetchRoles(session.access_token);
                }
                return;
            }

            if (session?.access_token && rolesLoadedForToken.current !== session.access_token) {
                setRolesLoading(true);
            }
            setSession(session ?? null);
            if (event === 'SIGNED_OUT') {
                clearCachedSettingsForSignedOutUser();
                setRoles([]);
                setRolesLoading(false);
                rolesLoadedForToken.current = null;
                return;
            }
            if (session?.access_token) {
                fetchRoles(session.access_token);
            }
        });

        return () => subscription.unsubscribe();
    }, [clearCachedSettingsForSignedOutUser, fetchRoles]);

    useEffect(() => {
        setRealtimeAccessTokenFactory(() => session?.access_token ?? null);
        return () => {
            setRealtimeAccessTokenFactory(null);
        };
    }, [session]);

    useEffect(() => {
        const token = session?.access_token || null;
        if (!token) return undefined;

        const maybeRefreshRoles = () => {
            if (rolesLoading) return;
            if (roles.length > 0) return;
            if (rolesLoadedForToken.current === token) return;
            fetchRoles(token);
        };

        const onFocus = () => {
            maybeRefreshRoles();
        };

        window.addEventListener('focus', onFocus);
        const intervalId = window.setInterval(maybeRefreshRoles, 30000);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.clearInterval(intervalId);
        };
    }, [fetchRoles, roles, rolesLoading, session]);

    const signIn = useCallback(async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return error?.message ?? null;
    }, []);

    const signUp = useCallback(async (email, password) => {
        const { error } = await supabase.auth.signUp({ email, password });
        return error?.message ?? null;
    }, []);

    const requestPasswordReset = useCallback(async (email) => {
        const redirectUrl = new URL('/reset-password', window.location.origin);
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: redirectUrl.toString()
        });
        return error?.message ?? null;
    }, []);

    const updatePassword = useCallback(async (password) => {
        const { error } = await supabase.auth.updateUser({ password });
        return error?.message ?? null;
    }, []);

    const signInWithGoogle = useCallback(async () => {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl') || '/select-meeting';
        const safeReturnUrl = returnUrl.startsWith('/') ? returnUrl : '/select-meeting';

        try {
            sessionStorage.setItem('regatta.oauth.returnUrl', safeReturnUrl);
        } catch {
            // Ignore storage failures and fall back to default redirect path handling.
        }

        const redirectUrl = new URL('/auth/callback', window.location.origin);

        return supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: redirectUrl.toString(),
                scopes: 'openid profile email'
            }
        });
    }, []);

    const signInWithAzure = useCallback(async () => {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl') || '/select-meeting';
        const safeReturnUrl = returnUrl.startsWith('/') ? returnUrl : '/select-meeting';

        try {
            sessionStorage.setItem('regatta.oauth.returnUrl', safeReturnUrl);
        } catch {
            // Ignore storage failures and fall back to default redirect path handling.
        }

        const redirectUrl = new URL('/auth/callback', window.location.origin);

        return supabase.auth.signInWithOAuth({
            provider: 'azure',
            options: {
                redirectTo: redirectUrl.toString(),
                scopes: 'openid profile email User.Read'
            }
        });
    }, []);

    const signInWithFacebook = useCallback(async () => {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl') || '/select-meeting';
        const safeReturnUrl = returnUrl.startsWith('/') ? returnUrl : '/select-meeting';

        try {
            sessionStorage.setItem('regatta.oauth.returnUrl', safeReturnUrl);
        } catch {
            // Ignore storage failures and fall back to default redirect path handling.
        }

        const redirectUrl = new URL('/auth/callback', window.location.origin);

        return supabase.auth.signInWithOAuth({
            provider: 'facebook',
            options: {
                redirectTo: redirectUrl.toString()
            }
        });
    }, []);

    const signOut = useCallback(async () => {
        clearCachedSettingsForSignedOutUser();
        return supabase.auth.signOut();
    }, [clearCachedSettingsForSignedOutUser]);

    const getToken = useCallback(() => session?.access_token ?? null, [session]);

    const hasRole = useCallback((role) => roles.includes(role), [roles]);

    const value = {
        session,
        user: session?.user ?? null,
        roles,
        rolesLoading,
        loading: session === undefined,
        isAdmin: roles.includes('Admin'),
        isCommentary: roles.includes('Commentary'),
        isStarter: roles.includes('StartBarge'),
        isFinishTent: roles.includes('FinishTent'),
        isRegistration: roles.includes('Registration'),
        isSponsorAdmin: roles.includes('SponsorAdmin'),
        signIn,
        signUp,
        requestPasswordReset,
        updatePassword,
        signInWithGoogle,
        signInWithAzure,
        signInWithFacebook,
        signOut,
        getToken,
        hasRole
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
